import React, { useEffect, useMemo, useState } from "react";
import ScrollHintRail from "global/components/ScrollHintRail/ScrollHintRail";
import {
  Box,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import strings from "global/constants/StringConstants";
import { DateRange } from "models/interfaces";
import { hasAccessTo } from "layouts/Config";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import Unauthorized from "screens/Shared/Unauthorized";
import TotalDocumentProgressGraph from "screens/Insights/components/TotalDocumentProgressGraph";

import RequestStatusPieChart from "./components/RequestStatusPieChart";
import { RequestStatusOptions } from "screens/MySignRequests/MySignRequestHelper";
import {
  fetchEnvelopeGraphData,
  fetchRequestStatusPieData,
  fetchTotalProgressData,
} from "./InsightsHelper";
import EnvelopeProgressGraph from "./components/EnvelopeProgressGraph";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import insightsStyles from "./Insights.styles";
import { insightsResponsiveTokens } from "./Insights.tokens";
import { useSetPageHeader } from "global/components/PageHeader/PageHeaderContext";
import AnalyticsViewToggle from "screens/Reports/components/AnalyticsViewToggle";
import { tourAttr } from "tour";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";

const styles = insightsStyles;
const { phone } = insightsResponsiveTokens;

/**
 * The two views form one tablist. Ids are fixed so every tab can point at the
 * panel it controls (`aria-controls`) and every panel back at its tab
 * (`aria-labelledby`) — WCAG 1.3.1 / 4.1.2.
 */
const TABS = [
  {
    label: "Workflow Trends",
    shortLabel: "Workflow Trends",
    tabId: "insights-tab-workflow",
    panelId: "insights-panel-workflow",
  },
  {
    label: "Progress & Status Distribution",
    // The chip rail and the tablet's tighter tab row print the reference's
    // shorter label; the desktop keeps the full one.
    shortLabel: "Status Distribution",
    tabId: "insights-tab-distribution",
    panelId: "insights-panel-distribution",
  },
];

const STATS_CONTROL_HEIGHT = 52;

/**
 * `Reports - Mobile & Tablet.dc.html` draws the workflow card as heading plus
 * chart alone — the status and date-range filters are desktop-only there, so
 * this row (and the card strapline that describes it) is hidden below `md`.
 */
const statsFilterRowSx = {
  width: "100%",
  minWidth: 0,
  display: { xs: "none", md: "grid" },
  gap: 2,
  gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
  alignItems: "center",
  "& > *": { minWidth: 0 },
};

const statusFilterSx = {
  "& .MuiAutocomplete-inputRoot": {
    minHeight: STATS_CONTROL_HEIGHT,
    height: STATS_CONTROL_HEIGHT,
    paddingTop: "0px !important",
    paddingBottom: "0px !important",
    paddingLeft: "16px !important",
    alignItems: "center",
    flexWrap: "nowrap",
  },
  "& .MuiOutlinedInput-root": {
    minHeight: STATS_CONTROL_HEIGHT,
    height: STATS_CONTROL_HEIGHT,
    borderRadius: "11px",
    flexWrap: "nowrap",
  },
  "& .MuiAutocomplete-endAdornment": { right: 12 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "divider",
  },
};

const statsDateBoxSx = {
  display: "flex",
  alignItems: "center",
  height: STATS_CONTROL_HEIGHT,
  px: 2,
  minWidth: 0,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "11px",
  bgcolor: "background.paper",
};

const statsDateTriggerSx = {
  minWidth: "0 !important",
  "& .MuiOutlinedInput-root": { p: 0 },
  // "& .MuiInputBase-input, & .MuiOutlinedInput-input": {  },
};

const progressDateTriggerSx = {
  minWidth: "0 !important",
  "& .MuiOutlinedInput-root": { justifyContent: "center" },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
    width: "auto",
    fieldSizing: "content",
    textAlign: "center",
  },
};

const InsightsPage = () => {
  useTitle(title.INSIGHTS);
  const insightsState = useFeatureState(FeatureKeys.REPORTING_ANALYTICS);
  const insightsAvailable =
    insightsState.status === strings.FEATURE_STATUS_AVAILABLE;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // The phone reference drops the page's own `<h1>` and carries "Insights" in
  // the app bar instead; the tablet keeps it on the page.
  useSetPageHeader(isPhone ? "Insights" : undefined);

  const hasFetchPermission = hasAccessTo(
    strings.resources.report,
    strings.fetchPermission,
  );

  const [isLineChartLoading, setIsLineChartLoading] = useState<boolean>(true);
  const [isTotalProgressLoading, setIsTotalProgressLoading] =
    useState<boolean>(true);
  const [isPieChartLoading, setIsPieChartLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [requestStatusData, setRequestStatusData] = useState<any[]>([]);
  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [
    openRequestStatusDateRangePicker,
    setOpenRequestStatusDateRangePicker,
  ] = useState(false);
  const [
    openTotalProgressDateRangePicker,
    setOpenTotalProgressDateRangePicker,
  ] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });
  const [pickerDateRange, setPickerDateRange] = useState<DateRange>(dateRange);
  const [totalProgressData, setTotalProgressData] = useState<any[]>([]);
  const [totalProgressDateRange, setTotalProgressDateRange] =
    useState<DateRange>({
      fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    });
  const [totalProgressPickerDateRange, setTotalProgressPickerDateRange] =
    useState<DateRange>(totalProgressDateRange);
  const [requestStatusDateRange, setRequestStatusDateRange] =
    useState<DateRange>({
      fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
    });
  const [requestStatusPickerDateRange, setRequestStatusPickerDateRange] =
    useState<DateRange>(requestStatusDateRange);
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const statusOptions = useMemo(
    () => [
      { label: "Sent", value: "sent" },
      { label: "Opened", value: "opened" },
      { label: "Completed", value: "completed" },
    ],
    [],
  );
  const [selectedStatus, setSelectedStatus] = useState<any[]>(statusOptions);
  const requestStatusOptions = useMemo(
    () => RequestStatusOptions.filter((item) => item.value !== "All"),
    [],
  );
  const [selectedRequestStatuses, setSelectedRequestStatuses] =
    useState<any[]>(requestStatusOptions);
  const requestStatusMetrics = useMemo(
    // () =>
    //   selectedRequestStatuses.length
    //     ? selectedRequestStatuses.map((item) => item.value)
    //     : requestStatusOptions.map((item) => item.value),
    // [selectedRequestStatuses, requestStatusOptions],

    () => selectedRequestStatuses.map((item) => item.value),
    [selectedRequestStatuses],
  );

  useEffect(() => {
    if (
      !insightsAvailable ||
      !hasFetchPermission ||
      !dateRange.fromDate ||
      !dateRange.toDate
    ) {
      setIsLineChartLoading(false);
      return;
    }
    const activeMetrics = selectedStatus.map((s) => s.value);
    if (activeMetrics.length === 0) {
      setChartData([]);
      setIsLineChartLoading(false);
      return;
    }
    setIsLineChartLoading(true);
    fetchEnvelopeGraphData(
      String(dateRange.fromDate),
      String(dateRange.toDate),
      activeMetrics,
      () => {},
      setChartData,
    ).finally(() => setIsLineChartLoading(false));
  }, [hasFetchPermission, dateRange, selectedStatus]);

  useEffect(() => {
    if (
      !insightsAvailable ||
      !hasFetchPermission ||
      !totalProgressDateRange.fromDate ||
      !totalProgressDateRange.toDate
    ) {
      setIsTotalProgressLoading(false);
      return;
    }
    setIsTotalProgressLoading(true);
    fetchTotalProgressData(
      String(totalProgressDateRange.fromDate),
      String(totalProgressDateRange.toDate),
      setTotalProgressData,
    ).finally(() => setIsTotalProgressLoading(false));
  }, [hasFetchPermission, totalProgressDateRange]);

  useEffect(() => {
    if (
      !insightsAvailable ||
      !hasFetchPermission ||
      !requestStatusDateRange.fromDate ||
      !requestStatusDateRange.toDate
    ) {
      setIsPieChartLoading(false);
      return;
    }

    if (requestStatusMetrics.length === 0) {
      setRequestStatusData([]);
      setIsPieChartLoading(false);
      return;
    }

    setIsPieChartLoading(true);
    fetchRequestStatusPieData(
      String(requestStatusDateRange.fromDate),
      String(requestStatusDateRange.toDate),
      requestStatusMetrics,
      setRequestStatusData,
    ).finally(() => setIsPieChartLoading(false));
  }, [hasFetchPermission, requestStatusDateRange, requestStatusMetrics]);

  if (!hasFetchPermission) {
    return <Unauthorized permissionDenied={!hasFetchPermission} />;
  }

  /**
   * The phone reference draws a compact sparkline, so the trends plot loses the
   * tablet's 320px. The real chart still carries its date axis and legend, so
   * it keeps enough room for both rather than the mock's bare 130px.
   */
  const chartSkeletonHeight = isPhone
    ? phone.card.chartHeight
    : isMobile
      ? 320
      : 400;
  const isWorkflowTabLoading = isLineChartLoading;
  const isStatusTabLoading = isTotalProgressLoading || isPieChartLoading;
  const isActiveTabLoading =
    activeTab === 0 ? isWorkflowTabLoading : isStatusTabLoading;

  const selectTab = (index: number) => {
    // Same guard the `disabled` prop used to provide — switching views while a
    // fetch is in flight stays blocked, but the tab keeps its focus instead of
    // going inert underneath the caret (WCAG 2.4.3).
    if (isActiveTabLoading) return;
    setActiveTab(index);
    tabRefs.current[index]?.focus();
  };

  // Arrow / Home / End movement is what makes a tablist a tablist to a screen
  // reader user (WCAG 2.1.1, ARIA APG "Tabs with automatic activation").
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const last = TABS.length - 1;
    let nextTab: number;

    switch (event.key) {
      case "ArrowRight":
        nextTab = activeTab === last ? 0 : activeTab + 1;
        break;
      case "ArrowLeft":
        nextTab = activeTab === 0 ? last : activeTab - 1;
        break;
      case "Home":
        nextTab = 0;
        break;
      case "End":
        nextTab = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    selectTab(nextTab);
  };

  if (insightsState.status !== strings.FEATURE_STATUS_AVAILABLE) {
    return (
      <CenteredAuthenticatedLayout>
        <Box sx={styles.page}>
          <FeatureGateBlock
            variant="full"
            featureName="Insights"
            status={insightsState.status as "not-in-plan" | "admin-disabled"}
            disabledMessage={(insightsState as any).disabledMessage}
          />
        </Box>
      </CenteredAuthenticatedLayout>
    );
  }

  return (
    <CenteredAuthenticatedLayout>
      <Box sx={styles.page}>
        <Box sx={styles.header}>
          <Box sx={styles.headerText}>
            <Typography component="h1" sx={styles.title}>
              Insights
            </Typography>
            <Typography component="p" sx={styles.subtitle}>
              Visualize workflow performance across your document requests.
            </Typography>
          </Box>
          {/* Inert from `md` up — the desktop reference has no such switch. */}
          <AnalyticsViewToggle current="insights" />
        </Box>

        <ScrollHintRail
          sx={styles.tabBarRail}
          railSx={styles.tabBar}
          railProps={{ role: "tablist", "aria-label": "Insights views" }}
          previousLabel="Show previous views"
          nextLabel="Show more views"
        >
          {TABS.map((tab, index) => (
            <ButtonBase
              key={tab.tabId}
              ref={(node: HTMLButtonElement | null) => {
                tabRefs.current[index] = node;
              }}
              id={tab.tabId}
              role="tab"
              aria-selected={activeTab === index}
              aria-controls={tab.panelId}
              // Roving tab stop: one Tab press reaches the tablist, arrows
              // move within it (ARIA APG).
              tabIndex={activeTab === index ? 0 : -1}
              aria-disabled={isActiveTabLoading || undefined}
              disableRipple
              onClick={() => selectTab(index)}
              onKeyDown={handleTabKeyDown}
              sx={styles.tab(activeTab === index)}
            >
              {isMobile ? tab.shortLabel : tab.label}
            </ButtonBase>
          ))}
        </ScrollHintRail>

        <Box sx={styles.panel}>
          {activeTab === 0 && (
            <Paper
              elevation={0}
              role="tabpanel"
              id={TABS[0].panelId}
              aria-labelledby={TABS[0].tabId}
              sx={styles.card(theme.palette.chart.cardShadow)}
            >
              <Stack spacing={{ xs: 1.625, sm: 1.875, md: 2 }}>
                <Stack spacing={0.5}>
                  <Typography
                    variant="subtitle1"
                    component="h2"
                    sx={styles.cardTitle()}
                  >
                    Workflow statistics
                  </Typography>
                  <Typography component="p" sx={styles.cardSubtitle}>
                    Select status and date range to update chart data.
                  </Typography>
                </Stack>

                <Box sx={statsFilterRowSx}>
                  <Box sx={{ minWidth: 0 }}>
                    <CustomAutoCompleteNew
                      multiple
                      clearIcon={false}
                      label=""
                      // The control shows no visible label, so the name has
                      // to be supplied explicitly (WCAG 4.1.2) — a
                      // placeholder is not a reliable accessible name.
                      ariaLabel="Filter workflow statistics by status"
                      id="insights-status-filter"
                      options={statusOptions}
                      value={selectedStatus}
                      disableCloseOnSelect
                      limitTags={1}
                      size="medium"
                      placeHolder="Filter by status"
                      sx={statusFilterSx}
                      getOptionLabel={(option: any) => option.label || ""}
                      renderTags={(value: any[]) => {
                        if (!value?.length) return null;
                        const [first, ...rest] = value;
                        return (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Chip
                              size="small"
                              label={first?.label || ""}
                              sx={{ height: 24, borderRadius: "10px" }}
                            />
                            {rest.length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +{rest.length}
                              </Typography>
                            )}
                          </Stack>
                        );
                      }}
                      renderOption={(
                        props: any,
                        option: any,
                        { selected }: any,
                      ) => (
                        <li {...props} style={{ padding: "4px 8px" }}>
                          {/* The option already exposes its state through
                                `aria-selected`; the tick is decoration, so it
                                is kept out of the tab order and the a11y tree
                                rather than read as a second, unnamed control. */}
                          <Checkbox
                            size="small"
                            style={{ marginRight: 8, padding: 4 }}
                            checked={selected}
                            tabIndex={-1}
                            inputProps={{
                              tabIndex: -1,
                              "aria-hidden": true,
                            }}
                          />
                          {option.label}
                        </li>
                      )}
                      onChange={(_: any, newValue: any) =>
                        setSelectedStatus(newValue || [])
                      }
                    />
                  </Box>
                  <Box sx={statsDateBoxSx}>
                    <CustomDateRangePicker
                      openDateRangePicker={openDateRangePicker}
                      setOpenDateRangePicker={setOpenDateRangePicker}
                      // Three pickers share this page; each needs a name of
                      // its own to be distinguishable (WCAG 4.1.2).
                      ariaLabel="Select date range for workflow statistics"
                      dateRange={pickerDateRange}
                      setDateRange={setPickerDateRange}
                      helperFunction={(confirmedRange: any) => {
                        setDateRange(confirmedRange);
                      }}
                      disableFuture={false}
                      lockFromDateToToDate
                      isClearable={false}
                      hideAllTime
                      sx={statsDateTriggerSx}
                    />
                  </Box>
                </Box>

                <Box
                  {...tourAttr("insights.chart")}
                  sx={styles.chartPanel()}
                  // Tells assistive tech the region is mid-update rather than
                  // empty while the filters refetch (WCAG 4.1.2).
                  aria-busy={isLineChartLoading}
                >
                  {isLineChartLoading ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: chartSkeletonHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress aria-label="Loading workflow statistics chart" />
                    </Box>
                  ) : (
                    <EnvelopeProgressGraph
                      data={chartData}
                      isMobile={isMobile}
                      chartHeight={chartSkeletonHeight}
                      selectedMetrics={selectedStatus.map((s) => s.value)}
                      embedded
                    />
                  )}
                </Box>
              </Stack>
            </Paper>
          )}

          {activeTab === 1 && (
            <Box
              role="tabpanel"
              id={TABS[1].panelId}
              aria-labelledby={TABS[1].tabId}
              sx={styles.statusGrid}
            >
              <Box {...tourAttr("insights.totalProgress")}>
                <Paper
                  elevation={0}
                  sx={styles.card(theme.palette.chart.cardShadow, "status")}
                >
                  <Stack spacing={{ xs: 1.625, sm: 1.875, md: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        sx={styles.cardTitle("status")}
                      >
                        Total document progress
                      </Typography>
                      <Typography component="p" sx={styles.cardSubtitle}>
                        Aggregate completion funnel for selected status and date
                        range.
                      </Typography>
                    </Stack>
                    {/* Desktop-only: the reference's phone and tablet card
                          is heading plus chart alone. */}
                    <Stack direction="row" sx={styles.filterRow}>
                      <Box
                        sx={{
                          width: "100%",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        <CustomDateRangePicker
                          openDateRangePicker={openTotalProgressDateRangePicker}
                          setOpenDateRangePicker={
                            setOpenTotalProgressDateRangePicker
                          }
                          ariaLabel="Select date range for total document progress"
                          dateRange={totalProgressPickerDateRange}
                          setDateRange={setTotalProgressPickerDateRange}
                          helperFunction={(confirmedRange: any) => {
                            setTotalProgressDateRange(confirmedRange);
                          }}
                          disableFuture={false}
                          lockFromDateToToDate
                          isClearable={false}
                          hideAllTime
                          autoSizeInput
                          sx={progressDateTriggerSx}
                        />
                      </Box>
                    </Stack>
                    <Box
                      sx={styles.chartPanel(chartSkeletonHeight)}
                      aria-busy={isTotalProgressLoading}
                    >
                      {isTotalProgressLoading ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: chartSkeletonHeight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress aria-label="Loading total document progress chart" />
                        </Box>
                      ) : (
                        <TotalDocumentProgressGraph
                          data={totalProgressData}
                          isMobile={isMobile}
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Box>
              <Box {...tourAttr("insights.requestStatus")}>
                <Paper
                  elevation={0}
                  sx={styles.card(theme.palette.chart.cardShadow, "status")}
                >
                  <Stack spacing={{ xs: 1.625, sm: 1.875, md: 2 }}>
                    <Stack spacing={0.5}>
                      <Typography
                        variant="subtitle1"
                        component="h2"
                        sx={styles.cardTitle("status")}
                      >
                        Request status distribution
                      </Typography>
                      <Typography component="p" sx={styles.cardSubtitle}>
                        Status-wise request split for selected date range on
                        created date.
                      </Typography>
                    </Stack>

                    {/* Desktop-only, as above. */}
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      gap={2}
                      alignItems={isMobile ? "stretch" : "center"}
                      justifyContent={isMobile ? "flex-start" : "space-between"}
                      flexWrap="wrap"
                      sx={styles.filterRowFull}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "100%" : 260,
                          flex: isMobile ? "1 1 100%" : "1 1 180px",
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        <CustomAutoCompleteNew
                          multiple
                          label=""
                          ariaLabel="Filter request status distribution by status"
                          id="insights-request-status-filter"
                          options={requestStatusOptions}
                          value={selectedRequestStatuses}
                          disableCloseOnSelect
                          limitTags={1}
                          size="medium"
                          placeHolder="Filter request statuses"
                          clearIcon={false}
                          sx={{
                            "& .MuiAutocomplete-inputRoot": {
                              minHeight: 56,
                              paddingTop: "0px !important",
                              paddingBottom: "0px !important",
                              alignItems: "center",
                            },
                            "& .MuiOutlinedInput-root": {
                              minHeight: 56,
                              flexWrap: "nowrap",
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "divider",
                            },
                          }}
                          getOptionLabel={(option: any) => option.label || ""}
                          renderTags={(value: any[]) => {
                            if (!value?.length) return null;
                            const [first, ...rest] = value;
                            return (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <Chip
                                  size="small"
                                  label={first?.label || ""}
                                  sx={{ height: 24, borderRadius: "10px" }}
                                />
                                {rest.length > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    +{rest.length}
                                  </Typography>
                                )}
                              </Stack>
                            );
                          }}
                          renderOption={(
                            props: any,
                            option: any,
                            { selected }: any,
                          ) => (
                            <li {...props} style={{ padding: "4px 8px" }}>
                              {/* Decorative tick — the option's own
                                    `aria-selected` already carries the state. */}
                              <Checkbox
                                size="small"
                                style={{ marginRight: 8, padding: 4 }}
                                checked={selected}
                                tabIndex={-1}
                                inputProps={{
                                  tabIndex: -1,
                                  "aria-hidden": true,
                                }}
                              />
                              {option.label}
                            </li>
                          )}
                          onChange={(_: any, newValue: any) =>
                            setSelectedRequestStatuses(newValue || [])
                          }
                        />
                      </Box>
                      <Box
                        sx={{
                          width: isMobile ? "100%" : 360,
                          flex: isMobile ? "1 1 100%" : "1 1 380px",
                          minWidth: 0,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "background.paper",
                        }}
                      >
                        <CustomDateRangePicker
                          openDateRangePicker={openRequestStatusDateRangePicker}
                          setOpenDateRangePicker={
                            setOpenRequestStatusDateRangePicker
                          }
                          ariaLabel="Select date range for request status distribution"
                          dateRange={requestStatusPickerDateRange}
                          setDateRange={setRequestStatusPickerDateRange}
                          helperFunction={(confirmedRange: any) => {
                            setRequestStatusDateRange(confirmedRange);
                          }}
                          disableFuture={false}
                          lockFromDateToToDate
                          isClearable={false}
                          hideAllTime
                        />
                      </Box>
                    </Stack>

                    <Box
                      sx={styles.chartPanel(chartSkeletonHeight)}
                      aria-busy={isPieChartLoading}
                    >
                      {isPieChartLoading ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: chartSkeletonHeight,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress aria-label="Loading request status distribution chart" />
                        </Box>
                      ) : (
                        <RequestStatusPieChart
                          data={requestStatusData}
                          selectedMetrics={selectedRequestStatuses.map(
                            (s) => s.value,
                          )}
                        />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </CenteredAuthenticatedLayout>
  );
};

export default InsightsPage;
